import { useEffect, useRef, useState } from "react";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import classNames from "classnames";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useNavigateToPortfolioTab } from "hooks/useNaviateToPortfolioTab";
import { AVATAR_COLORS } from "layout/ContactSwitcherVariants";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { Navigate, useParams } from "react-router-dom";

const portfolioBadge = (item: PortfolioOption | undefined): string => {
  if (!item || !item.typeCode) return "AL";
  return item.typeCode.startsWith("2") ? "CG" : "NN";
};

export const PortfolioSelect = () => {
  const { selectedContactId } = useGetContractIdData();
  const { data, loading } = useGetContactInfo(false, selectedContactId);
  const { t } = useModifiedTranslation();
  const portfolios = data?.portfolios?.sort((a, b) => a.type.code.localeCompare(b.type.code)) || [];

  const { portfolioId: portfolioIdUrl } = useParams();
  const portfolioId = portfolioIdUrl ? parseInt(portfolioIdUrl, 10) : 0;
  const navigateToPortfolioTab = useNavigateToPortfolioTab();

  const portfolioItems: PortfolioOption[] = [
    ...(portfolios.length > 1
      ? [{ id: 0, label: t("navTab.totalInvestments"), urlPrefix: "", shortName: "", typeCode: "" }]
      : []),
    ...portfolios.map((x) => ({
      id: x.id,
      label: x.name,
      urlPrefix: `/portfolio/${x.id}`,
      shortName: x.shortName,
      typeCode: x.type.code,
    })),
  ];

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) setOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  if (portfolioId !== 0 && !loading && !portfolios.some((x) => x.id === portfolioId)) {
    return <Navigate to="/" replace />;
  }

  if (!loading && (!portfolioItems || portfolioItems.length === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 pl-1 pr-2.5 py-1">
        <div className="w-7 h-7 rounded-full bg-capGreen/20 animate-pulse shrink-0" />
        <div className="hidden sm:block h-3.5 w-28 lg:w-40 rounded-full bg-capGreen/20 animate-pulse" />
      </div>
    );
  }

  const selected = portfolioItems.find((x) => x.id === portfolioId) ?? portfolioItems[0];
  const selectedIndex = portfolioItems.findIndex((x) => x.id === portfolioId);
  const selectedColor = AVATAR_COLORS[Math.max(selectedIndex, 0) % AVATAR_COLORS.length];
  const badgeLabel = portfolioBadge(selected);

  return (
    <div ref={ref} className="relative" onBlur={handleBlur}>
      <button
        onClick={() => portfolioItems.length > 1 && setOpen((v) => !v)}
        className={classNames(
          "flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 transition-colors focus:outline-none",
          {
            "hover:bg-capGreen/10 cursor-pointer": portfolioItems.length > 1,
            "cursor-default": portfolioItems.length <= 1,
          }
        )}
      >
        <span
          className="w-7 h-7 leading-none rounded-full text-xs font-semibold text-white flex items-center justify-center shrink-0"
          style={{ backgroundColor: selectedColor }}
        >
          {badgeLabel}
        </span>
        <span className="hidden sm:block text-sm font-medium text-capGreen max-w-[10rem] lg:max-w-[20rem] truncate">
          {selected?.label}
        </span>
        {portfolioItems.length > 1 && (
          <svg
            className={classNames("w-3 h-3 text-capGreen/60 transition-transform", {
              "rotate-180": open,
            })}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-64 bg-capBeige border border-capGreen/20 rounded-lg shadow-lg z-[1100] py-1">
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-capGreen/50">
            {t("navTab.totalInvestments")}
          </p>
          {portfolioItems.map((item, index) => {
            const isSelected = item.id === portfolioId;
            const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
            const badge = portfolioBadge(item);
            return (
              <button
                key={item.id}
                onClick={() => {
                  setOpen(false);
                  navigateToPortfolioTab(item.urlPrefix);
                }}
                className={classNames(
                  "w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-capGreen/10",
                  { "font-semibold": isSelected },
                  { "border-b border-capGreen/10 mb-1": item.id === 0 }
                )}
              >
                <span
                  className={classNames(
                    "w-6 h-6 rounded-full text-[10px] font-semibold text-white flex items-center justify-center shrink-0",
                    { "opacity-60": item.id === 0 }
                  )}
                  style={{ backgroundColor: color }}
                >
                  {badge}
                </span>
                <span className="flex flex-col min-w-0">
                  <span
                    className={classNames(
                      "truncate",
                      item.id === 0 ? "text-capGreen/60 italic" : "text-capGreen"
                    )}
                  >
                    {item.id === 0 ? "Samlet oversikt" : item.label}
                  </span>
                  {item.shortName && (
                    <span className="truncate text-[11px] text-capGreen/50">{item.shortName}</span>
                  )}
                </span>
                {isSelected && (
                  <svg
                    className="ml-auto w-3.5 h-3.5 text-capGreen shrink-0"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path d="M2 7l4 4 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export interface PortfolioOption {
  id: number;
  urlPrefix: string;
  label: string;
  shortName: string;
  typeCode: string;
}
